.editor {
  background-color: #fff;
  border: 1px solid #d8dee4;
  border-radius: 12px;
  box-shadow: 0 2px 8px rgba(27, 31, 36, 0.08);
  overflow: hidden;
}

.tabs {
  align-items: flex-end;
  border-bottom: 1px solid #d8dee4;
  display: flex;
  gap: 24px;
  padding: 0 20px;
}

.tab {
  background: transparent;
  border: 0;
  border-bottom: 3px solid transparent;
  color: #57606a;
  cursor: pointer;
  font-size: 15px;
  font-weight: 600;
  margin-bottom: -1px;
  padding: 15px 4px 12px;
  transition: border-color 120ms ease, color 120ms ease;
}

.tab:hover {
  color: #24292f;
}

.activeTab {
  border-bottom-color: #0d6efd;
  color: #0d6efd;
}

.toolbar {
  align-items: center;
  border-bottom: 1px solid #e1e5ea;
  display: flex;
  flex-wrap: wrap;
  gap: 10px;
  padding: 12px 16px;
}

.headingControl {
  display: inline-flex;
  position: relative;
}

.headingSelect {
  appearance: none;
  background-color: #fff;
  border: 1px solid #d0d7de;
  border-radius: 6px;
  color: #343a40;
  cursor: pointer;
  font-size: 14px;
  font-weight: 500;
  height: 38px;
  min-width: 132px;
  padding: 6px 34px 6px 12px;
}

.headingSelect:hover:not(:disabled) {
  background-color: #f8f9fa;
  border-color: #adb5bd;
}

.headingSelect:disabled {
  background-color: #f1f3f5;
  cursor: not-allowed;
  opacity: 0.65;
}

.headingChevron {
  color: #6c757d;
  pointer-events: none;
  position: absolute;
  right: 9px;
  top: 10px;
}

.toolbarGroup {
  align-items: center;
  border-left: 1px solid #e1e5ea;
  display: flex;
  gap: 6px;
  padding-left: 10px;
}

.toolbarButton {
  align-items: center;
  background-color: #fff;
  border: 1px solid #d0d7de;
  border-radius: 6px;
  color: #495057;
  cursor: pointer;
  display: inline-flex;
  height: 38px;
  justify-content: center;
  min-width: 38px;
  padding: 0 9px;
  transition: background-color 120ms ease, border-color 120ms ease,
    box-shadow 120ms ease, color 120ms ease;
}

.labeledToolbarButton {
  gap: 7px;
  min-width: auto;
  padding: 0 12px;
}

.toolbarButton:hover:not(:disabled) {
  background-color: #f0f6ff;
  border-color: #86b7fe;
  color: #0b5ed7;
}

.toolbarButton:active:not(:disabled) {
  background-color: #e7f1ff;
  box-shadow: inset 0 1px 2px rgba(27, 31, 36, 0.12);
}

.toolbarButton:disabled {
  background-color: #f8f9fa;
  color: #adb5bd;
  cursor: not-allowed;
  opacity: 0.7;
}

.tab:focus-visible,
.headingSelect:focus-visible,
.toolbarButton:focus-visible {
  box-shadow: 0 0 0 3px rgba(13, 110, 253, 0.2);
  outline: 2px solid #0d6efd;
  outline-offset: 1px;
}

.surface {
  padding: 14px 16px 16px;
}

.textarea {
  background-color: #fff;
  border: 1px solid #d8dee4;
  border-radius: 7px;
  color: #24292f;
  display: block;
  font-family: ui-monospace, SFMono-Regular, Menlo, Monaco, Consolas,
    'Liberation Mono', monospace;
  font-size: 15px;
  line-height: 1.65;
  min-height: clamp(460px, 58vh, 700px);
  padding: 18px 20px;
  resize: vertical;
  width: 100%;
}

.textarea::placeholder {
  color: #6c757d;
}

.textarea:focus {
  border-color: #0d6efd;
  box-shadow: 0 0 0 3px rgba(13, 110, 253, 0.18);
  outline: 0;
}

.textarea:disabled {
  background-color: #f8f9fa;
  color: #6c757d;
  cursor: not-allowed;
}

.previewPanel {
  border: 1px solid #d8dee4;
  border-radius: 7px;
  line-height: 1.65;
  margin: 14px 16px 16px;
  min-height: clamp(460px, 58vh, 700px);
  overflow-wrap: anywhere;
  padding: 20px 24px;
}

.status {
  align-items: center;
  border-top: 1px solid #e1e5ea;
  color: #57606a;
  display: flex;
  font-size: 13px;
  justify-content: space-between;
  min-height: 48px;
  padding: 10px 20px;
}

.supportedStatus {
  align-items: center;
  color: #57606a;
  display: inline-flex;
  font-weight: 500;
  gap: 8px;
}

.statusCheckIcon {
  color: #198754;
}

.invalidStatus {
  align-items: center;
  color: #842029;
  display: inline-flex;
  font-weight: 500;
  gap: 8px;
}

.invalidStatusIcon {
  align-items: center;
  background-color: #dc3545;
  border-radius: 50%;
  color: #fff;
  display: inline-flex;
  font-size: 11px;
  font-weight: 700;
  height: 18px;
  justify-content: center;
  width: 18px;
}

.characterCount {
  font-variant-numeric: tabular-nums;
}

.emptyPreview {
  color: #6c757d;
  font-style: italic;
  margin: 0;
}

.markdownContent {
  color: #24292f;
  overflow-wrap: anywhere;
  white-space: pre-wrap;
}

.markdownContent > :first-child {
  margin-top: 0;
}

.markdownContent > :last-child {
  margin-bottom: 0;
}

.markdownContent h1,
.markdownContent h2,
.markdownContent h3,
.markdownContent h4 {
  font-weight: 600;
  line-height: 1.3;
  margin-bottom: 0.55em;
  margin-top: 1.25em;
}

.markdownContent h1 {
  border-bottom: 1px solid #d8dee4;
  font-size: 1.75em;
  padding-bottom: 0.3em;
}

.markdownContent h2 {
  border-bottom: 1px solid #d8dee4;
  font-size: 1.45em;
  padding-bottom: 0.3em;
}

.markdownContent h3 {
  font-size: 1.2em;
}

.markdownContent h4 {
  font-size: 1em;
}

.markdownContent p,
.markdownContent ul,
.markdownContent ol,
.markdownContent pre,
.markdownContent table,
.markdownContent blockquote {
  margin-bottom: 1em;
  margin-top: 0;
}

.markdownContent ul,
.markdownContent ol {
  padding-left: 2em;
}

.markdownContent li + li {
  margin-top: 0.25em;
}

.markdownContent blockquote {
  border-left: 4px solid #ced4da;
  color: #57606a;
  margin-left: 0;
  padding-left: 16px;
}

.markdownContent code {
  background-color: rgba(175, 184, 193, 0.2);
  border-radius: 4px;
  font-size: 0.9em;
  padding: 2px 4px;
}

.markdownContent pre {
  background-color: #f6f8fa;
  border-radius: 6px;
  overflow-x: auto;
  padding: 14px;
  white-space: pre;
}

.markdownContent pre code {
  background: transparent;
  padding: 0;
}

.markdownContent table {
  border-collapse: collapse;
  display: block;
  max-width: 100%;
  overflow-x: auto;
  white-space: normal;
  width: max-content;
}

.markdownContent th,
.markdownContent td {
  border: 1px solid #d0d7de;
  padding: 6px 12px;
}

.markdownContent th {
  background-color: #f6f8fa;
  font-weight: 600;
}

.markdownContent input[type='checkbox'] {
  margin-right: 6px;
}

.markdownContent hr {
  border: 0;
  border-top: 1px solid #d8dee4;
  margin: 1.5em 0;
}

.unsafeMarkdown {
  color: #842029;
  margin: 0;
}

.visuallyHidden {
  border: 0;
  clip: rect(0 0 0 0);
  height: 1px;
  margin: -1px;
  overflow: hidden;
  padding: 0;
  position: absolute;
  white-space: nowrap;
  width: 1px;
}

@media (max-width: 760px) {
  .toolbar {
    gap: 8px;
  }

  .toolbarGroup {
    border-left: 0;
    padding-left: 0;
  }

  .textarea,
  .previewPanel {
    min-height: 420px;
  }

  .status {
    align-items: flex-start;
    flex-direction: column;
    gap: 4px;
  }
}

@media (prefers-reduced-motion: reduce) {
  .tab,
  .toolbarButton {
    transition: none;
  }
}
